/* eslint-disable react/no-array-index-key */
import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import {
  Accordion,
  AlertMessage,
  Column,
  ComponentType,
  convertDateToStringIgnoringTimezone,
  convertISOStringToLocalDateString,
  createTableCell,
  createTableColumn,
  DISPLAY_DATE_FORMAT,
  FileTypes,
  Form,
  FormCheckbox,
  FormErrorDisplay,
  FormFileUploader,
  FormText,
  IBreadcrumbItem,
  Icon,
  IconName,
  IFileUploaderValue,
  lowerFirstLetter,
  ResponsivePaginatedTable,
  Scrollbar,
  ToastMessage,
  toastMessage,
  useForm,
} from '@gcl/react-core';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import {
  useFetchCheckerCompanyOptions,
  useFetchDesignerCompanyOptions,
  useFetchReviewerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { Note } from '@core/Note/Note';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import React, { useCallback, useEffect } from 'react';
import { useFetchTeamsOptionsByCompanyTypeIdOrganizationTypeId } from '@projectTeam/projectTeam.queries';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import { IProjectTeamOption } from '@projectTeam/projectTeam.models';
import * as XLSX from 'xlsx';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { useFetchAllVoItems } from '@voItem/voItem.queries';
import { isMatch } from 'date-fns';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IVoItem } from '@voItem/voItem.models';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosErrorResponse } from '@core/error.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { KEY_SEPARATOR } from '@common/constants';
import { keySeparator } from '@core/Primitives/text.services';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { MasterScheduleDetailResponsiblePersonsAndTeams } from './detail/MasterScheduleDetailResponsiblePersonsAndTeams';
import {
  ICreateBatchProjectMasterScheduleBaseRequest,
  ICreateBatchProjectMasterScheduleExcelRow,
  ICreateBatchProjectMasterScheduleFormState,
} from './masterScheduleBatchAdd.models';
import {
  BATCH_ADD_FILETYPE,
  EMasterScheduleBatchAddFormKey,
  EMasterScheduleBatchAddJsonKey,
} from './masterScheduleBatchAdd.constants';
import { useBatchAddProjectMasterSchedules } from './masterSchedule.queries';
import { EMasterScheduleFormKey, IResponsibleUser } from './detail/masterScheduleDetail.models';

export const MasterScheduleBatchAddPage = () => {
  const { navigate, projectNo, generateRoutePath } = useNavigate();
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const { formState } = useFormMethods;

  const { isDirty, dirtyFields } = formState;

  useEffect(() => {
    console.log('dirty', isDirty, dirtyFields);
  }, [isDirty, dirtyFields]);

  const watchFile = useFormMethods.watch(EMasterScheduleBatchAddFormKey.FILE);
  const watchMasterSchedules = (useFormMethods.watch(EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES) ??
    []) as ICreateBatchProjectMasterScheduleBaseRequest[];

  useEffect(() => {
    useFormMethods.clearErrors(EMasterScheduleBatchAddFormKey.FILE);
    useFormMethods.resetField(EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES);
    if (watchFile) onChangeFile(watchFile);
  }, [watchFile]);

  const { data: dataAllRiskCategories } = (
    useFetchAllRiskCategories({ isDirty: formState.isDirty }) as () => UseQueryResult<
      IRiskCategory[],
      AxiosErrorResponse
    >
  )();
  const { data: dataAllVoItems } = (
    useFetchAllVoItems({ isDirty: formState.isDirty }) as () => UseQueryResult<IVoItem[], AxiosErrorResponse>
  )();

  const { data: dataSiteOperationTeamOptions, isLoading: isLoadingSiteOperationTeams } =
    useFetchTeamsOptionsByCompanyTypeIdOrganizationTypeId({
      shouldConvertOptions: false,
    })(
      { projectId, companyTypeId: ECompanyTypeIdConst.SITE_OPERATION, organizationTypeId: EOrganizationTypeId.SITE },
      !formState?.isDirty,
    );
  const { data: dataDesignerCompanyOptions, isLoading: isLoadingTeamsDesigner } = useFetchDesignerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);
  const { data: dataCheckerCompanyOptions, isLoading: isLoadingTeamsChecker } = useFetchCheckerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);
  const { data: dataReviewCompanyOptions, isLoading: isLoadingTeamsReviewer } = useFetchReviewerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);

  const {
    isLoading: isLoadingBatchAddProjectMasterSchedules,
    isSuccess: isSuccessBatchAddProjectMasterSchedules,
    isError: isErrorBatchAddProjectMasterSchedules,
    error: errorBatchAddProjectMasterSchedules,
    reset: resetBatchAddProjectMasterSchedules,
    mutate: mutateBatchAddProjectMasterSchedules,
  } = useBatchAddProjectMasterSchedules();

  const handleSubmit = (data: any) => {
    const dataLocal: ICreateBatchProjectMasterScheduleFormState = data;
    console.log(dataLocal);

    const convertUsers = (users: IResponsibleUser[]) => {
      return users.map((s) => {
        return { projectId: s.projectId, projectUserProfileId: s.projectUserProfileId };
      });
    };
    const convertTeams = (teams: IProjectTeamOption[]) => {
      return teams.map((s) => {
        return { ...s, id: undefined, projectTeamId: s.id };
      });
    };

    const request = {
      ...data,
      [EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES]: (
        data[EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES] as ICreateBatchProjectMasterScheduleBaseRequest[]
      ).map((s) => {
        return {
          ...s,
          [EMasterScheduleFormKey.VO_ITEM]:
            s[EMasterScheduleFormKey.VO_ITEM]?.name !== undefined ? s[EMasterScheduleFormKey.VO_ITEM] : undefined,
        };
      }),
      [EMasterScheduleFormKey.RESPONSIBLE_SRP]: convertUsers(
        (data[EMasterScheduleFormKey.RESPONSIBLE_SRP] ?? []) as IResponsibleUser[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_TWC]: convertUsers(
        (data[EMasterScheduleFormKey.RESPONSIBLE_TWC] ?? []) as IResponsibleUser[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_TWCM]: convertUsers(
        (data[EMasterScheduleFormKey.RESPONSIBLE_TWCM] ?? []) as IResponsibleUser[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM]: convertTeams(
        (data[EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM] ?? []) as IProjectTeamOption[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM]: convertTeams(
        (data[EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM] ?? []) as IProjectTeamOption[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM]: convertTeams(
        (data[EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM] ?? []) as IProjectTeamOption[],
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM]: convertTeams(
        (data[EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM] ?? []) as IProjectTeamOption[],
      ),
    };
    mutateBatchAddProjectMasterSchedules(request);
  };

  const validateXlsx = useCallback(
    (rows: ICreateBatchProjectMasterScheduleExcelRow[]) => {
      const rowErrors = rows.map((s, index) => {
        const errorMsg: string[] = [];
        if (
          !s[EMasterScheduleBatchAddJsonKey.T1_REF_NO]?.length ||
          s[EMasterScheduleBatchAddJsonKey.T1_REF_NO]?.length <= 0
        )
          errorMsg.push('Missing T1 No.');
        if (
          !s[EMasterScheduleBatchAddJsonKey.DESIGN_PACKAGE]?.length ||
          s[EMasterScheduleBatchAddJsonKey.DESIGN_PACKAGE]?.length <= 0
        )
          errorMsg.push('Missing Design Package');

        if (
          !s[EMasterScheduleBatchAddJsonKey.RISK_CATEGORY]?.length ||
          s[EMasterScheduleBatchAddJsonKey.RISK_CATEGORY]?.length <= 0
        ) {
          errorMsg.push('Missing Risk Category');
        } else if (
          !dataAllRiskCategories?.find((r) => r?.category === s[EMasterScheduleBatchAddJsonKey.RISK_CATEGORY])
        ) {
          errorMsg.push(`Invalid Risk Category ${s[EMasterScheduleBatchAddJsonKey.RISK_CATEGORY]}`);
        }
        if (
          !!s[EMasterScheduleBatchAddJsonKey.VO_ITEM] &&
          s[EMasterScheduleBatchAddJsonKey.VO_ITEM].length > 0 &&
          !dataAllVoItems?.find((v) => v.name === s[EMasterScheduleBatchAddJsonKey.VO_ITEM])
        ) {
          errorMsg.push(`Invalid VO Item ${s[EMasterScheduleBatchAddJsonKey.VO_ITEM]}`);
        }
        if (
          !!s[EMasterScheduleBatchAddJsonKey.REQUIRE_WORK_PROCEDURE_DRAWING] &&
          !['Y', 'N'].includes(s[EMasterScheduleBatchAddJsonKey.REQUIRE_WORK_PROCEDURE_DRAWING])
        )
          errorMsg.push('Invalid Require Work Procedure Drawing');

        if (
          !!s[EMasterScheduleBatchAddJsonKey.FALSEWORK] &&
          !['Y', 'N'].includes(s[EMasterScheduleBatchAddJsonKey.FALSEWORK])
        )
          errorMsg.push('Invalid Falsework');

        if (
          !!s[EMasterScheduleBatchAddJsonKey.PROCUREMENT_DATE] &&
          s[EMasterScheduleBatchAddJsonKey.PROCUREMENT_DATE].length > 0 &&
          !isMatch(s[EMasterScheduleBatchAddJsonKey.PROCUREMENT_DATE], DISPLAY_DATE_FORMAT)
        )
          errorMsg.push('Invalid Procurement/Fabrication Date format');

        if (
          !!s[EMasterScheduleBatchAddJsonKey.START_CONSTRUCTION_DATE] &&
          s[EMasterScheduleBatchAddJsonKey.START_CONSTRUCTION_DATE].length > 0 &&
          !isMatch(s[EMasterScheduleBatchAddJsonKey.START_CONSTRUCTION_DATE], DISPLAY_DATE_FORMAT)
        )
          errorMsg.push('Invalid Start Construction Date format');

        if (
          !!s[EMasterScheduleBatchAddJsonKey.REMOVAL_OR_REDUNDANT_DATE] &&
          s[EMasterScheduleBatchAddJsonKey.REMOVAL_OR_REDUNDANT_DATE].length > 0 &&
          !isMatch(s[EMasterScheduleBatchAddJsonKey.REMOVAL_OR_REDUNDANT_DATE], DISPLAY_DATE_FORMAT)
        )
          errorMsg.push('Invalid Removal/Redundant Date format');

        return errorMsg;
      });

      const errorMsg = rowErrors.map((rowError, index) => {
        if (rowError.length <= 0) return undefined;
        return `Row ${index + 2}: ${rowError.join(', ')}`;
      });

      return errorMsg.filter((s): s is string => !!s).join('. \n');
    },
    [JSON.stringify(dataAllRiskCategories), JSON.stringify(dataAllVoItems)],
  );

  const convertXlsxRows = useCallback(
    (rows: ICreateBatchProjectMasterScheduleExcelRow[]) => {
      return rows.map((s) => {
        const riskCategory = dataAllRiskCategories?.find(
          (r) => r.category === s[EMasterScheduleBatchAddJsonKey.RISK_CATEGORY],
        );
        const voItem = dataAllVoItems?.find((v) => v.name === s[EMasterScheduleBatchAddJsonKey.VO_ITEM]);

        const converted: ICreateBatchProjectMasterScheduleBaseRequest = {
          projectId,
          refNo: s[EMasterScheduleBatchAddJsonKey.T1_REF_NO] ?? '',
          header: s[EMasterScheduleBatchAddJsonKey.T1_HEADER],
          designPackage: s[EMasterScheduleBatchAddJsonKey.DESIGN_PACKAGE] ?? '',
          riskCategory: { ...riskCategory } as IRiskCategory,
          voItem,
          requiredWorkProcedureDrawings: s[EMasterScheduleBatchAddJsonKey.REQUIRE_WORK_PROCEDURE_DRAWING] === 'Y',
          isFalsework: s[EMasterScheduleBatchAddJsonKey.FALSEWORK] === 'Y',
          procurementDate: s[EMasterScheduleBatchAddJsonKey.PROCUREMENT_DATE]
            ? convertDateToStringIgnoringTimezone(new Date(s[EMasterScheduleBatchAddJsonKey.PROCUREMENT_DATE]))
            : undefined,
          startConstructionDate: s[EMasterScheduleBatchAddJsonKey.START_CONSTRUCTION_DATE]
            ? convertDateToStringIgnoringTimezone(new Date(s[EMasterScheduleBatchAddJsonKey.START_CONSTRUCTION_DATE]))
            : undefined,
          removalDate: s[EMasterScheduleBatchAddJsonKey.REMOVAL_OR_REDUNDANT_DATE]
            ? convertDateToStringIgnoringTimezone(new Date(s[EMasterScheduleBatchAddJsonKey.REMOVAL_OR_REDUNDANT_DATE]))
            : undefined,
          remarks: s[EMasterScheduleBatchAddJsonKey.REMARKS],
        };

        return converted;
      });
    },
    [JSON.stringify(dataAllRiskCategories), JSON.stringify(dataAllVoItems)],
  );

  const onChangeFile = useCallback(
    async (newValue: IFileUploaderValue | null) => {
      useFormMethods.clearErrors(EMasterScheduleBatchAddFormKey.FILE);
      if (!newValue) throw new Error('No file uploaded');
      const file = newValue?.files[0];
      if (!file.name.endsWith(BATCH_ADD_FILETYPE))
        toastMessage(
          <ToastMessage
            message="Only .xlsx file is accepted"
            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
          />,
        );

      const data = await file.arrayBuffer();

      const workbook = XLSX.read(data, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        dateNF: DISPLAY_DATE_FORMAT,
        defval: null,
      });

      const errorMsg = validateXlsx(jsonData as ICreateBatchProjectMasterScheduleExcelRow[]);
      if (errorMsg.length > 0) {
        useFormMethods.setError(EMasterScheduleBatchAddFormKey.FILE, { message: errorMsg });
        return;
      }

      const convertedRows = convertXlsxRows(jsonData as ICreateBatchProjectMasterScheduleExcelRow[]);
      useFormMethods.setValue(EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES, convertedRows);
    },
    [validateXlsx, convertXlsxRows],
  );

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: 'Search T1',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(generateRoutePath(routes.t1.path));
      },
    },
    {
      prefixIconName: 'JournalText' as IconName,
      text: `Batch Add T1`,
      active: true,
    },
  ];

  const topBarLeftItems: (IDetailPageTopBarItem | React.ReactElement)[] = React.useMemo(() => {
    return [
      {
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Primary,
        onClick: () => {
          useFormMethods.handleSubmit(handleSubmit)();
        },
        isLoading: isLoadingBatchAddProjectMasterSchedules,
        isError: isErrorBatchAddProjectMasterSchedules,
        isSuccess: isSuccessBatchAddProjectMasterSchedules,
        title: 'Submit',
        showNoneInReadOnly: true,
        buttonIconName: 'FolderCheck' as IconName,
      },
    ];
  }, [
    isLoadingBatchAddProjectMasterSchedules,
    isSuccessBatchAddProjectMasterSchedules,
    isErrorBatchAddProjectMasterSchedules,
    handleSubmit,
  ]);

  const masterScheduleColumns: Column[] = React.useMemo(() => {
    return [
      createTableColumn({
        accessor: EMasterScheduleFormKey.REF_NO,
        Header: 'T1 No.',
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormText
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.REF_NO}`}
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: 'header',
        Header: 'T1 Header',
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.DESIGN_PACKAGE,
        Header: 'Design Package',
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormText
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.DESIGN_PACKAGE}`}
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),

      createTableColumn({
        accessor: EMasterScheduleFormKey.FALSEWORK,
        Header: 'Is Falsework',
        disableResizing: false,
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormCheckbox
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.FALSEWORK}`}
                disabled
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS,
        Header: 'Require Work Procedure Drawing',
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormCheckbox
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS}`}
                disabled
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: `${EMasterScheduleFormKey.RISK_CATEGORY}.category`,
        Header: 'Risk Category',
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormText
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.RISK_CATEGORY}.category`}
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: `${EMasterScheduleFormKey.VO_ITEM}.name`,
        Header: 'VO Item',
        Cell: createTableCell<boolean, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: boolean, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <FormText
                id={`${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.VO_ITEM}.name`}
                formFieldMethods={useFormMethods}
              />
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.PROCUREMENT_DATE,
        Header: 'Procurement / Fabrication',
        Cell: createTableCell<string, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: string, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <div>
                <span>{item.procurementDate ? convertISOStringToLocalDateString(item.procurementDate) : ''}</span>
                <FormErrorDisplay
                  error={
                    useFormMethods.getFieldState(
                      `${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.PROCUREMENT_DATE}`,
                    ).error
                  }
                />
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.START_CONSTRUCTION_DATE,
        Header: 'Start Construction',
        Cell: createTableCell<string, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: string, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <div>
                <span>
                  {item.startConstructionDate ? convertISOStringToLocalDateString(item.startConstructionDate) : ''}
                </span>
                <FormErrorDisplay
                  error={
                    useFormMethods.getFieldState(
                      `${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.PROCUREMENT_DATE}`,
                    ).error
                  }
                />
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.REMOVAL_DATE,
        Header: 'Removal / Redundant',
        Cell: createTableCell<string, ICreateBatchProjectMasterScheduleBaseRequest>(
          (value: string, item: ICreateBatchProjectMasterScheduleBaseRequest, index: number) => {
            return (
              <div>
                <span>{item.removalDate ? convertISOStringToLocalDateString(item.removalDate) : ''}</span>
                <FormErrorDisplay
                  error={
                    useFormMethods.getFieldState(
                      `${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${EMasterScheduleFormKey.PROCUREMENT_DATE}`,
                    ).error
                  }
                />
              </div>
            );
          },
        ),
      } as Column),
      createTableColumn({
        accessor: EMasterScheduleFormKey.REMARKS,
        Header: 'Remarks',
      } as Column),
    ];
  }, []);

  return (
    <Scrollbar>
      <DetailPageLayout
        errorGetDetail={null}
        isDrawer={false}
        data={{}}
        isRefetching={false}
        isLoading={false}
        title={createDefaultDetailPageTitleFn(
          <>
            <span className="gcl-my-sm">Batch Add T1</span>
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={() => {
          return (
            <Form
              defaultValues={{
                callback: () => {
                  const siteOperationTeamDefaultOptions = (dataSiteOperationTeamOptions ?? [])
                    .filter((o: any) => o.isDefault === true)
                    .map((s) => {
                      return { ...s, ...s.data } as IProjectTeamOption;
                    });

                  const state: ICreateBatchProjectMasterScheduleFormState = {
                    responsibleSiteOperationTeams: siteOperationTeamDefaultOptions,
                    masterSchedules: [],
                    hasNoDesigner: null,
                    remarkForNoDesigner: null,
                    responsibleDesigner: null,
                    responsibleChecker: null,
                    responsibleReviewer: null,
                    responsibleDesignerTeams: [],
                    responsibleCheckerTeams: [],
                    responsibleReviewerTeams: [],
                    responsibleSrp: [],
                    responsibleTwc: [],
                    responsibleTwcm: [],
                  };
                  return state;
                },
                deps: [dataSiteOperationTeamOptions],
              }}
              useFormMethods={useFormMethods}
              render={(formFieldMethods) => {
                return (
                  <>
                    <DetailPageTopBar leftItems={topBarLeftItems} />
                    <ApiSuccessToast
                      successes={[
                        {
                          isSuccess: isSuccessBatchAddProjectMasterSchedules,
                          reset: resetBatchAddProjectMasterSchedules,
                          message: 'Batch add success',
                        },
                      ]}
                      callbackAfterToasted={() => {
                        navigate(routes.t1.path);
                      }}
                    />
                    <ApiErrorDisplay
                      errors={[
                        {
                          error: errorBatchAddProjectMasterSchedules,
                          genericError: 'Batch add fail',
                          reset: resetBatchAddProjectMasterSchedules,
                          keyExtractor: (originalKey: string) => {
                            const { key, id: index } = keySeparator(originalKey, KEY_SEPARATOR);
                            if (!key) return originalKey;
                            return `${EMasterScheduleBatchAddFormKey.MASTER_SCHEDULES}[${index}].${lowerFirstLetter(
                              key,
                            )}`;
                          },
                        },
                      ]}
                      setFormError={useFormMethods.setError}
                      callbackAfterToast={() => {}}
                    />
                    <ol>
                      <li>
                        <a
                          href="/templates/MasterScheduleBatchAddTemplate.xlsx"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download Excel Template
                        </a>{' '}
                        for Batch Add T1.
                      </li>
                      <li>Fill in package Information for each T1 to be added.</li>
                      <li>Upload the Excel file.</li>
                      <li>Fill in responsible persons and teams</li>
                      <li>Press &apos;Submit&apos; button </li>
                    </ol>
                    <div>
                      <FormFileUploader
                        disabled={isProjectArchived}
                        formFieldMethods={formFieldMethods}
                        id={EMasterScheduleBatchAddFormKey.FILE}
                        acceptFileTypes={[FileTypes.Xlsx]}
                        renderError={(error) => {
                          return (
                            <>
                              {error?.message?.split('\n').map((s, index) => (
                                <AlertMessage
                                  key={`file-error-${index}`}
                                  className="gcl-form-error-display"
                                  content={s}
                                  dismissible={false}
                                  type={ComponentType.Danger}
                                />
                              ))}
                            </>
                          );
                        }}
                      />
                    </div>
                    {watchMasterSchedules.length > 0 && (
                      <Accordion
                        allowClickHeaderToExpand
                        className="gcl-mt-lg"
                        content={{
                          id: `gcl-gems-t1-detail-page-accordion Responsible Persons and Teams`,
                          onRenderBody: () => {
                            return (
                              <ResponsivePaginatedTable
                                allowSelectRow={false}
                                columns={masterScheduleColumns}
                                items={watchMasterSchedules}
                                disablePagination
                              />
                            );
                          },
                          onRenderHeader: 'Master Schedules',
                          defaultIsExpanded: true,
                        }}
                      />
                    )}

                    <Accordion
                      allowClickHeaderToExpand
                      className="gcl-mt-lg"
                      content={{
                        id: `gcl-gems-t1-detail-page-accordion Responsible Persons and Teams`,
                        onRenderBody: () => {
                          return (
                            <>
                              <Note
                                className="gcl-fb-md gcl-mb-lg"
                                componentType={ComponentType.Danger}
                                prefixIcon={<Icon iconName={'ExclamationCircle' as IconName} />}
                              >
                                The following selected responsible presons and teams will be applied to all added T1.
                              </Note>
                              <MasterScheduleDetailResponsiblePersonsAndTeams
                                formFieldMethods={formFieldMethods}
                                checkerCompanyOptions={dataCheckerCompanyOptions ?? []}
                                designerCompanyOptions={dataDesignerCompanyOptions ?? []}
                                reviewCompanyOptions={dataReviewCompanyOptions ?? []}
                                siteOperationTeamOptions={dataSiteOperationTeamOptions ?? []}
                                projectId={projectId}
                                isReadOnly={isProjectArchived}
                                mode="Create"
                              />
                            </>
                          );
                        },
                        onRenderHeader: 'Responsible Persons and Teams',
                        defaultIsExpanded: true,
                      }}
                    />
                  </>
                );
              }}
            />
          );
        }}
      />
    </Scrollbar>
  );
};
